'use client';

/**
 * FilterSidebar — the Explore / Private left filter sidebar.
 *
 * The presentational shell (sticky desktop aside + mobile drawer, item rows,
 * count pills, active state, all data-cy hooks) is now the published se3
 * design-system package (`@startengine/design-system.se3.filter-sidebar`,
 * exported as `Se3FilterSidebar`).
 *
 * The discover-specific demoData wiring stays HERE, per the refactor guidance:
 * the design system carries no demo data, so this adapter computes per-item
 * live counts from `SIDE_GROUPS` / `SECTIONS` / `FILTER_META` (`countFor` /
 * `matchCompanies`), applies the `view` ('open' → Reg CF/A+ only) filter, and
 * passes the resulting `groups` (each item enriched with `count` + `showCount`)
 * to the package. `onSelect` fires `{ group, item }` with the original
 * `SideItem` preserved, so call sites keep working unchanged.
 *
 * NOTE: This is NOT the locked design-system `top-nav` / search flow.
 */
import { useRef, type FC } from 'react';
import { Se3FilterSidebar as Se3FilterSidebarRaw } from '@startengine/design-system.se3.filter-sidebar';
import { styled } from '@mui/material/styles';
import {
  SIDE_GROUPS,
  FILTER_META,
  SECTIONS,
  isOpenToAll,
  type OfferingSection,
  type OfferingCardData,
  type SideItem,
  type SideItemType,
} from 'components/HomepageV3/data/demoData';
import { COLORS } from 'components/HomepageV3/theme/tokens';
import { buildSe3ExploreRoute, useSe3QaSearch } from 'components/HomepageV3/utils/routes';

/** Minimum-filter split point (Paige, 2026-07-07): Under $5K vs $5K+. */
export const MIN_BUCKET_THRESHOLD = 5000;

/** Parse a numeric minimum off a card's "Minimum" figure, if present. */
export function cardMinimum(card: OfferingCardData): number | undefined {
  const fig = [card.figL, card.figR].find((f) => f && /minimum/i.test(f.lbl));
  if (!fig) return undefined;
  const parsed = Number(String(fig.val).replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Bucket an offering into the two Minimum tiers. The real per-offering minimum
 * decides `under-5k` vs `5k-plus`; when unknown, fall back to reg type —
 * crowdfunding (Reg CF/A+) → under $5K, private (Reg D) → $5K+.
 */
export function minimumBucketKey(
  minimum: number | undefined,
  crowd: boolean,
): 'under-5k' | '5k-plus' {
  if (minimum !== undefined) return minimum < MIN_BUCKET_THRESHOLD ? 'under-5k' : '5k-plus';
  return crowd ? 'under-5k' : '5k-plus';
}

export type SidebarView = 'private' | 'open';

export interface FilterSelection {
  /** Group label the item belongs to (e.g. "Browse by"). */
  group: string;
  item: SideItem;
}

export interface FilterSidebarProps {
  /** Drives which offerings are counted: `open` filters to Reg CF / Reg A+ only. */
  view?: SidebarView;
  /** Browse shelves used for Browse by counts. Defaults to demoData SECTIONS. */
  sections?: OfferingSection[];
  /**
   * Sidebar layout:
   *  - `all`   → Explore: groups render as collapsible dropdowns.
   *  - `home`  → Homepage: groups render expanded (flat), and the Industries
   *    group gets an "Explore more industries →" CTA to the Explore page
   *    (Paige QA batch 3 — the full industry taxonomy lives on Explore).
   *  - `browse` → Browse-only (expanded Browse rows derived from `sections`).
   */
  filterMode?: 'all' | 'browse' | 'home';
  /**
   * Dynamic industry rows (Explore). When provided they REPLACE the curated
   * demoData "Industries" list — derived FE-only from the live offering pool ∩
   * the canonical industry taxonomy (Paige QA batch 3). Each carries its own
   * count; matching is by category name (`liveInd`), not demoData FILTER_META.
   */
  industries?: { label: string; key: string; count: number }[];
  /** Currently active item key (`null` shows the default browse view). */
  activeKey?: string | null;
  onSelect?: (selection: FilterSelection) => void;
  /** Clears any active filter (returns to the full browse view). */
  onClear?: () => void;
  /** Homepage handoff hides the sidebar entirely on mobile; Explore keeps it. */
  showMobileToggle?: boolean;
  className?: string;
  'data-cy'?: string;
}

/** Companies matching an `ind` / `acc` / `min` filter (mirrors source `matchCos`). */
export function matchCompanies(type: SideItemType, key: string, view: SidebarView): string[] {
  const keys = Object.keys(FILTER_META);
  let res: string[] = [];
  if (type === 'ind') {
    res = keys.filter((k) => FILTER_META[k].ind === key);
  } else if (type === 'acc') {
    res = keys.filter((k) => FILTER_META[k].acc === key);
  } else if (type === 'min') {
    res = keys.filter((k) => FILTER_META[k].min === key);
  }
  if (view === 'open') res = res.filter(isOpenToAll);
  return res;
}

/** Number of offerings behind a sidebar item (mirrors source `countFor`). */
export function countFor(
  item: SideItem,
  view: SidebarView,
  sections: OfferingSection[] = SECTIONS,
): number {
  if (item.type === 'browse') {
    const section = sections.find((s) => s.title === item.key);
    if (!section) return 0;
    const cards = view === 'open' ? section.cards.filter((c) => isOpenToAll(c.co)) : section.cards;
    return cards.length;
  }
  if (item.type === 'min') {
    const seen = new Set<string>();
    let count = 0;
    sections.forEach((section) =>
      section.cards.forEach((card) => {
        if (seen.has(card.co)) return;
        const crowd = isOpenToAll(card.co);
        if (view === 'open' && !crowd) return;
        seen.add(card.co);
        if (minimumBucketKey(cardMinimum(card), crowd) === item.key) count += 1;
      }),
    );
    return count;
  }
  return matchCompanies(item.type, item.key, view).length;
}

interface SidebarGroupItem extends SideItem {
  count: number;
  showCount: boolean;
  sourceKey?: string;
}

interface SidebarGroup {
  label: string;
  sourceLabel?: string;
  items: SidebarGroupItem[];
  /** Render each group as a dropdown that expands its items on click. */
  collapsible?: boolean;
  /** Optional link rendered after the group's items (e.g. "Explore more industries →"). */
  cta?: { label: string; href: string };
}

interface Se3FilterSidebarProps {
  groups: SidebarGroup[];
  activeKey?: string | null;
  onSelect?: (selection: { group: string; item: SideItem }) => void;
  onClear?: () => void;
  className?: string;
  'data-cy'?: string;
}

const Se3FilterSidebar = Se3FilterSidebarRaw as unknown as FC<Se3FilterSidebarProps>;

const SIDEBAR_STICKY_TOP_PX = 52;
const SIDEBAR_STICKY_BOTTOM_GAP_PX = 32;
const SIDEBAR_STICKY_MAX_HEIGHT = `calc(100vh - ${
  SIDEBAR_STICKY_TOP_PX + SIDEBAR_STICKY_BOTTOM_GAP_PX
}px)`;

const SidebarWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'showMobileToggle' && prop !== 'activeGroupIndex',
})<{ showMobileToggle: boolean; activeGroupIndex: number }>(
  ({ showMobileToggle, activeGroupIndex }) => ({
    display: 'contents',
    '& > aside': {
      boxSizing: 'border-box',
      position: 'sticky',
      top: `${SIDEBAR_STICKY_TOP_PX}px`,
      alignSelf: 'start',
      maxHeight: SIDEBAR_STICKY_MAX_HEIGHT,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      scrollbarGutter: 'stable',
    },
    ...(showMobileToggle
      ? null
      : {
          '& [data-cy="filter-sidebar-mobile"]': {
            display: 'none !important',
          },
        }),
    ...(activeGroupIndex >= 0
      ? {
          [`& [data-cy="filter-sidebar-section"]:nth-of-type(${activeGroupIndex + 1}) > [data-cy="filter-sidebar-group-toggle"]`]:
            {
              background: COLORS.night,
              borderColor: COLORS.night,
              color: COLORS.card,
              '&:hover': {
                background: COLORS.night2,
                borderColor: COLORS.night2,
              },
            },
        }
      : {}),
  }),
);

/** Build the prop-driven `groups` the se3 package renders (counts baked in). */
function buildGroups(
  view: SidebarView,
  sections: OfferingSection[],
  filterMode: FilterSidebarProps['filterMode'],
  industries?: FilterSidebarProps['industries'],
  qaSearch?: string,
): SidebarGroup[] {
  const browseGroup: SidebarGroup = {
    label: 'Browse by',
    items: sections.map((section) => ({
      label: section.title,
      type: 'browse',
      key: section.title,
      count: countFor({ label: section.title, type: 'browse', key: section.title }, view, sections),
      showCount: true,
    })),
  };

  // Explore replaces the curated demoData industries with live rows (`liveInd`),
  // matched by category name rather than FILTER_META (Paige QA batch 3).
  const liveIndustryItems: SidebarGroupItem[] = (industries ?? []).map((industry) => ({
    label: industry.label,
    type: 'liveInd',
    key: industry.key,
    count: industry.count,
    showCount: true,
  }));

  const sourceGroups =
    filterMode === 'browse'
      ? [browseGroup]
      : SIDE_GROUPS.map((group) => {
          if (group.label === 'Browse by') {
            return {
              ...group,
              items: group.items.filter((item) => countFor(item, view, sections) > 0),
            };
          }
          if (group.label === 'Industries' && industries) {
            return { ...group, items: liveIndustryItems };
          }
          return group;
        });

  const asDropdowns = filterMode === 'all';
  const isHome = filterMode === 'home';

  const filterGroups = sourceGroups
    .map((group) => {
      // Homepage (Josh QA): drop the curated industry LIST and show a single
      // "Explore by industry →" link to the Explore page (the full taxonomy
      // lives on Explore). The se3 filter-sidebar package renders a link-only
      // group (empty items + cta) from v0.0.6.
      if (isHome && group.label === 'Industries') {
        return {
          label: group.label,
          items: [],
          cta: {
            label: 'Explore by industry →',
            href: buildSe3ExploreRoute(undefined, qaSearch),
          },
        };
      }
      return {
        label: group.label,
        ...(asDropdowns ? { collapsible: true } : {}),
        items: group.items
          .map((item) => ({
            ...item,
            // `liveInd` counts are supplied by the caller (live pool); everything
            // else is computed from demoData / the injected sections.
            count:
              item.type === 'liveInd'
                ? ((item as SidebarGroupItem).count ?? 0)
                : countFor(item, view, sections),
            showCount: item.type !== 'min',
          }))
          .filter((entry) => !(view === 'open' && entry.count === 0)),
      };
    })
    .filter((group) => group.items.length > 0 || Boolean(group.cta));

  return filterGroups;
}

function withActiveFilterSelection(
  groups: SidebarGroup[],
  activeKey: string | null,
  filterMode: FilterSidebarProps['filterMode'],
): SidebarGroup[] {
  if (!activeKey || filterMode !== 'all') return groups;

  return groups.map((group) => {
    const activeItem = group.items.find((item) => item.key === activeKey);
    if (!activeItem) return group;

    return {
      ...group,
      label: activeItem.label,
      sourceLabel: group.label,
      // The design-system dropdown forces itself open when activeKey matches a
      // row. Give the displayed selected row a UI-only key so the field can
      // close normally; sourceKey is restored before notifying the parent.
      items: group.items.map((item) =>
        item.key === activeKey
          ? { ...item, key: `__selected-filter__${item.key}`, sourceKey: item.key }
          : item,
      ),
    };
  });
}

function FilterSidebar({
  view = 'private',
  sections = SECTIONS,
  filterMode = 'all',
  industries,
  activeKey = null,
  onSelect,
  onClear,
  showMobileToggle = true,
  className,
  'data-cy': dataCy = 'filter-sidebar',
}: FilterSidebarProps) {
  const qaSearch = useSe3QaSearch();
  const groups = buildGroups(view, sections, filterMode, industries, qaSearch);
  const displayGroups = withActiveFilterSelection(groups, activeKey, filterMode);
  const activeGroupIndex = displayGroups.findIndex((group) => Boolean(group.sourceLabel));
  const sidebarRootRef = useRef<HTMLDivElement>(null);

  /**
   * The design-system sidebar owns each desktop dropdown's expanded state. On
   * selection, explicitly toggle the originating group closed so a filter
   * choice behaves like a conventional select menu rather than trapping the
   * user in the open list. This remains in the discover adapter because the
   * selected filter and route state are discover-specific.
   */
  const closeSelectedGroup = (groupLabel?: string) => {
    const toggles = sidebarRootRef.current?.querySelectorAll<HTMLElement>(
      '[data-cy="filter-sidebar-group-toggle"][aria-expanded="true"]',
    );
    Array.from(toggles ?? [])
      .filter((element) => !groupLabel || element.getAttribute('aria-label') === groupLabel)
      .forEach((toggle) => toggle.click());
  };

  return (
    <SidebarWrapper
      ref={sidebarRootRef}
      showMobileToggle={showMobileToggle}
      activeGroupIndex={activeGroupIndex}
    >
      <Se3FilterSidebar
        groups={displayGroups}
        activeKey={activeKey}
        onSelect={({ group, item }: { group: string; item: SideItem }) => {
          const sourceGroup = displayGroups.find((entry) => entry.label === group)?.sourceLabel;
          const { sourceKey, ...displayItem } = item as SidebarGroupItem;
          const sourceItem = sourceKey ? { ...displayItem, key: sourceKey } : item;
          onSelect?.({ group: sourceGroup ?? group, item: sourceItem });
          closeSelectedGroup(group);
        }}
        onClear={() => {
          onClear?.();
          closeSelectedGroup();
        }}
        className={className}
        data-cy={dataCy}
      />
    </SidebarWrapper>
  );
}

export default FilterSidebar;
